import React from 'react';
import { authentication, zapier_authentication } from './helpImages';
import { REDIRECT_URLS } from './openApi-constant';

export const ZAPIER_INTEGRATION_DOC_TITLES = [
    {
        question: 'Getting Started',
        answer: (
            <>
                <p>
                    Getting started with the ReferUs Zapier integration takes only a few minutes.
                    Follow the steps below to connect your ReferUs account and begin building
                    automated referral workflows.
                </p>
            </>
        ),
        children: [
            {
                question: 'Step 1: Create or Log In to Zapier',
                answer: (
                    <>
                        <p>
                            To get started, visit the Zapier login page at{' '}
                            <a
                                href="https://zapier.com/app/login"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                https://zapier.com/app/login
                            </a>.
                        </p>

                        <p>
                            If you already have a Zapier account, log in using your existing credentials.
                            If you do not have an account, sign up first and then proceed to log in.
                        </p>

                        <p>
                            <img
                                src={zapier_authentication[0]}
                                alt="Zapier login page email"
                                style={{ width: '100%', marginTop: '10px' }}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                            <br />
                            <p>
                                Once logged in successfully, you will be redirected to the Zapier dashboard,
                                where you can start creating Zaps.
                            </p>
                            <img
                                src={zapier_authentication[1]}
                                alt="Zapier dashboard"
                                style={{ width: '100%', marginTop: '10px' }}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                        </p>
                    </>
                ),
            },

            {
                question: 'Step 2: Ensure You Have an Active ReferUs Account',
                answer: (
                    <>
                        <p>
                            Before connecting to Zapier, make sure you have an active ReferUs  <strong> business account </strong> with the necessary permissions to create referrers, referrals,
                            and payouts.
                        </p>

                        <p>
                            During authentication, users are required to log in with valid
                            ReferUs business credentials. If no business account exists,
                            authentication cannot be completed.
                        </p>

                        <p>
                            If you do not already have a business registered, create one directly
                            from the ReferUs website:
                        </p>

                        <pre>
                            <code>{REDIRECT_URLS?.registerUrl}</code>
                        </pre>

                        <img
                            src={authentication[2]}
                            alt="business registeration page"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />

                        <p>
                            Once business registration is complete, the account can be used to log in,
                            review permissions, and approve access during the OAuth flow.
                        </p>
                    </>
                ),
            },
            {
                question: 'Step 3: Find ReferUs on Zapier',
                answer: (
                    <>
                        <p>
                            This step guides you through creating a new Zap and locating ReferUs
                            inside the Zap Editor.
                        </p>

                        <p><strong>Step 3.1: Open the Zap Creation Flow</strong></p>

                        <p>From the Zapier dashboard:</p>
                        <ul>
                            <li>Go to <strong>Assets</strong> from the left navigation</li>
                            <li>Click <strong>Create</strong></li>
                            <li>Select <strong>Zap</strong></li>
                        </ul>

                        <img
                            src={zapier_authentication[2]}
                            alt="business registeration page"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />

                        <p>
                            You may optionally create or select a folder to organize your
                            automations. Otherwise, proceed without creating a folder.
                        </p>

                        <p><strong>Step 3.2: Access the Zap Editor</strong></p>

                        <p>
                            The Zap Editor opens in a new tab.
                        </p>

                        <p>
                            Inside the Zap Editor, Zapier will prompt you to choose how the app
                            should be used within your automation.
                        </p>

                        <p>
                            Zapier apps can be used in two ways:
                        </p>

                        <ul>
                            <li>
                                <strong>Triggers</strong> – Start a Zap when an event occurs in an app
                            </li>
                            <li>
                                <strong>Actions</strong> – Perform an operation in an app as part of a Zap
                            </li>
                        </ul>

                        <p>
                            Currently, ReferUs supports <strong>Actions only</strong>. This means
                            ReferUs is used to perform operations after another
                            app triggers the workflow.
                        </p>

                        <img
                            src={zapier_authentication[3]}
                            alt="zapier editor page"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />

                        <p><strong>Step 3.3: Search and Select ReferUs</strong></p>

                        <p>
                            To proceed:
                        </p>

                        <ul>
                            <li>Select <strong>Action</strong> when prompted</li>
                            <li>Search for <strong>ReferUs</strong> in the app selection screen</li>
                        </ul>

                        <img
                            src={zapier_authentication[4]}
                            alt="zapier refer us search"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />

                        <ul>
                            <li>Select <strong>ReferUs</strong> to view the available actions</li>
                        </ul>

                        <p>
                            You can then choose the ReferUs action that best fits your workflow,
                            such as creating a referrer, sending a referral etc.
                        </p>

                        <img
                            src={zapier_authentication[5]}
                            alt="zapier action selection"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </>
                ),
            },
        ],
    },
    {
        question: 'Authentication',
        answer: (
            <>
                <p>
                    Authentication allows Zapier to securely access ReferUs on behalf of your business.
                    ReferUs uses <strong>OAuth 2.0</strong> for Zapier authentication.
                </p>

                <p>
                    You do not need to manage API keys manually when using Zapier.
                </p>

                <h4>Connecting ReferUs to Zapier</h4>

                <p>To connect ReferUs with Zapier:</p>

                <ol>
                    <li>Log in to your Zapier account</li>
                    <li>Create a new Zap</li>
                    <li>Select ReferUs as the action app</li>
                </ol>

                <img
                    src={zapier_authentication[10]}
                    alt="ReferUs action test response"
                    style={{ width: '100%', marginTop: '10px' }}
                    onContextMenu={(e) => e.preventDefault()}
                />

                <p>Select an account if already authenticated</p>
                <p>OR</p>

                <ul>
                    <li>Click <strong>Connect a new account</strong></li>
                    <li>You will be redirected to ReferUs</li>
                    <li>Log in and approve Zapier access</li>
                </ul>

                <h4>Login & Consent</h4>

                <ul>
                    <li>User logs in using their ReferUs business account.</li>
                </ul>

                <img
                    src={authentication[3]}
                    alt="ReferUs login screen"
                    style={{ width: '100%', marginTop: '10px' }}
                    onContextMenu={(e) => e.preventDefault()}
                />

                <ul>
                    <li>User reviews consent screen.</li>
                    <li>User clicks <strong>Allow</strong> to approve access.</li>
                </ul>

                <img
                    src={zapier_authentication[6]}
                    alt="referus login success"
                    style={{ width: '100%', marginTop: '10px' }}
                    onContextMenu={(e) => e.preventDefault()}
                />

                <p>
                    Once approved, your ReferUs account will be securely connected to Zapier
                    using OAuth 2.0.
                </p>

                <img
                    src={zapier_authentication[7]}
                    alt="zapier connect refer us"
                    style={{ width: '100%', marginTop: '10px' }}
                    onContextMenu={(e) => e.preventDefault()}
                />
            </>
        ),
    },
    {
        question: 'Available ReferUs Actions',
        answer: (
            <>
                <p>
                    After connecting your ReferUs account, the following actions become available
                    inside Zapier.
                </p>
            </>
        ),
        children: [
            {
                question: 'Create Referrer',
                answer: (
                    <>
                        <p>
                            Creates a referrer automatically in ReferUs.
                        </p>
                        <ul>
                            <li>Common first step in referral workflows</li>
                            <li>Triggered when a new user is added in another system</li>
                        </ul>
                    </>
                ),
            },
            {
                question: 'Get Rewards',
                answer: (
                    <>
                        <p>
                            Retrieves available rewards configured in ReferUs business.
                        </p>
                        <p>
                            The returned <code>id</code> should be used as:
                        </p>
                        <ul>
                            <li><code>rewardId</code> when sending referrals</li>
                            <li><code>offerId</code> when paying referrals</li>
                        </ul>
                    </>
                ),
            },
            {
                question: 'Send Referral',
                answer: (
                    <>
                        <p>
                            First, use the <strong>Get Rewards</strong> action to retrieve the available rewards.
                            The selected reward ID will later be passed into the <strong>Send Referral</strong> action.
                        </p>

                        <p>
                            After setting up the trigger, create a new action and search for &nbsp;
                            <strong>Formatter</strong> in Zapier.
                        </p>

                        <p>
                            Select <strong>Utilities</strong> as the action event.
                        </p>

                        <p>
                            In the configuration step:
                        </p>

                        <ul>
                            <li>In the <strong>Transform</strong> dropdown, choose <strong>Pick from List</strong>.</li>
                            <li>In the <strong>Operation</strong> field, select <strong>First</strong>.</li>
                            <li>
                                In the <strong>Input</strong> field, click the <strong>+</strong> icon and map the
                                reward response from the <strong>Get Rewards</strong> step (select the response key).
                            </li>
                        </ul>

                        <p>
                            Test the step to ensure one reward record is returned.
                        </p>

                        <p>
                            Finally, in the <strong>Send Referral</strong> action, map the &nbsp;
                            <strong>Reward ID</strong> field using the output from the&nbsp;
                            <strong>Formatter (Utilities)</strong> step.
                        </p>

                        <p>
                            This workflow sends a referral invitation from a referrer to a referral email,
                            including the selected reward.
                        </p>
                    </>

                ),
            },
            {
                question: 'Update Referral Status',
                answer: (
                    <>
                        <p>
                            The <strong>Update Referral Status</strong> action allows an authenticated business
                            to update the status of an existing referral (for example:
                            <em>acknowledged</em>, <em>contacted</em>, <em>qualified</em>, or <em>not_a_fit</em>).
                            This is typically used when the business reviews or responds to a referral.
                        </p>

                        <p>
                            Status updates must follow the correct sequence:
                        </p>

                        <ul>
                            <li><strong>acknowledged</strong></li>
                            <li><strong>contacted</strong></li>
                            <li><strong>qualified</strong> or <strong>not_a_fit</strong></li>
                        </ul>

                        <p>
                            If the status transition order is not followed, the system will return an error.
                        </p>
                    </>

                ),
            },
            {
                question: 'Get Referrals',
                answer: (
                    <>
                        <p>
                            The Get Referrals action retrieves a list of referrals associated with the authenticated business or referrer. By default, it returns referrals that are qualified and not yet paid, making it ideal for payout tracking and reporting workflows.
                        </p>
                    </>
                ),
            },
            {
                question: 'Pay Referral',
                answer: (
                    <>
                        <p>
                            The <strong>Pay Referral action</strong> allows a business to mark a
                            qualified referral as paid. This action records the payout,
                            updates the referral status, and triggers notification emails
                            to the referrer.
                        </p>

                        <h4>Recommended Flow</h4>
                        <ol>
                            <li>
                                Fetch qualified referrals using the <strong>Get Referrals action</strong>.
                            </li>
                            <li>
                                Use <strong>Formatter by Zapier (Utilities)</strong> to extract a
                                single referral email from the Get Referrals response:
                                <ul>
                                    <li>Select <strong>Utilities</strong> as the action event.</li>
                                    <li>Choose <strong>Pick from List</strong> in the Transform field.</li>
                                    <li>Select <strong>First</strong> as the operation.</li>
                                    <li>
                                        In the Input field, map the <strong>Email</strong> value from the
                                        Get Referrals response data (which contains multiple emails).
                                    </li>
                                </ul>
                                After testing, the Formatter step will return one email,
                                which can then be mapped in the next step.
                            </li>
                            <li>
                                Select a reward using the <strong>Get Rewards action</strong>&nbsp;
                                (you may follow the same Formatter process used in the Send Referral flow).
                            </li>
                            <li>
                                Call the <strong>Pay Referral action</strong> and map:
                                <ul>
                                    <li>The referral email (from Formatter)</li>
                                    <li>The reward ID</li>
                                </ul>
                            </li>
                        </ol>
                    </>
                ),
            },
            {
                question: 'Pay Referrer',
                answer: (
                    <>
                        <p>
                            The <strong>Pay Referrer</strong> allows a business to pay a referrer after
                            successful referral payouts. It clears outstanding balances and records payment history.
                        </p>

                        <h4>Recommended Flow</h4>
                        <ol>
                            <li>Ensure referrals are paid (<code>success</code>).</li>
                            {/* <li>Verify referrer has outstanding balance.</li> */}
                            <li>Call <strong>Pay Referrer action</strong>.</li>
                        </ol>
                    </>
                ),
            },
        ],
    },
    {
        question: 'Example Workflow',
        answer: (
            <>
                <p>
                    Below are practical end-to-end automation examples built using ReferUs with Zapier.
                    These workflows demonstrate how referrals can be created, tracked, and
                    paid automatically.
                </p>
            </>
        ),
        children: [
            {
                question: 'Workflow 1: Create Referrer from Referral Rock New Member Signup',
                answer: (
                    <>
                        <p>
                            This workflow automatically creates a Referrer in ReferUs
                            when a new member signs up in Referral Rock.
                        </p>

                        <p>
                            <strong>Trigger:</strong> New Member Signup
                        </p>

                        <p>
                            <strong>Action:</strong> Create Referrer (ReferUs)
                        </p>

                        <ul>
                            <li>A new member signs up through the Referral Rock program page</li>
                            <li>The Zap is triggered automatically</li>
                            <li>ReferUs creates a new Referrer using member details</li>
                        </ul>

                        <p>
                            Once published, every new Referral Rock member signup
                            will automatically be registered as a Referrer in ReferUs.
                        </p>

                        <img
                            src={zapier_authentication[18]}
                            alt="Referral Rock trigger setup"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />

                        <img
                            src={zapier_authentication[19]}
                            alt="ReferUs action setup"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </>
                ),
            },

            {
                question: 'Workflow 2: New Referral to Pay Referral (Referral Rock Trigger)',
                answer: (
                    <>
                        <p>
                            This workflow demonstrates a complete referral lifecycle,
                            automatically triggered when a new referral is created in Referral Rock.
                        </p>

                        <p>
                            <strong>Trigger:</strong> New Referral Created (Referral Rock)
                        </p>

                        <p>
                            <strong>ReferUs Actions:</strong>
                        </p>

                        <ul>
                            <li>Send Referral</li>
                            <li>Update Referral Status to <strong>Qualified</strong></li>
                            <li>Get Referrals</li>
                            <li>Pay Referral</li>
                            <li>Pay Referrer</li>
                        </ul>

                        <p>
                            When a new referral is created in Referral Rock,
                            Zapier automatically executes the full referral
                            processing and payout workflow in ReferUs.
                        </p>

                        <img
                            src={zapier_authentication[13]}
                            alt="New Referral trigger setup"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                        <img
                            src={zapier_authentication[15]}
                            alt="ReferUs workflow actions"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                        <img
                            src={zapier_authentication[14]}
                            alt="Referral payout setup"
                            style={{ width: '100%', marginTop: '10px' }}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </>
                ),
            },

        ],
    },
    {
        question: 'Best Practices',
        answer: (
            <>
                <p>
                    Follow these best practices to ensure reliable and scalable referral
                    automations using ReferUs on Zapier.
                </p>

                <ul>
                    <li>
                        Always retrieve rewards dynamically using the <strong>Get Rewards</strong> action
                    </li>
                    <li>
                        Store IDs (such as<code>rewardId</code>) between steps
                        for reuse in later actions
                    </li>
                    <li>
                        Use <strong>Zapier Filters</strong> to prevent invalid or incomplete executions
                    </li>
                    <li>
                        Test each ReferUs action individually before publishing the Zap
                    </li>
                </ul>
            </>
        ),
    },
    {
        question: 'Testing and Environments',
        answer: (
            <>
                <p>
                    Proper testing ensures your Zap behaves as expected before it is used
                    in a live environment.
                </p>

                <ul>
                    <li>
                        Use test or staging ReferUs accounts during initial Zap configuration
                    </li>
                    <li>
                        Validate all field mappings during Zapier test runs
                    </li>
                    <li>
                        Review returned data from ReferUs actions for accuracy
                    </li>
                    <li>
                        Switch to production ReferUs credentials only after verification
                    </li>
                </ul>
            </>
        ),
    },
    {
        question: 'Support',
        answer: (
            <>
                <p>
                    If you require assistance while integrating ReferUs with Zapier, the
                    following resources are available:
                </p>

                <ul>
                    <li>Review ReferUs action descriptions inside Zapier</li>
                    <li>Follow this ReferUs Zapier integration documentation</li>
                    <li>Contact ReferUs support for additional help</li>
                </ul>

                <p>
                    <strong>ReferUs</strong> — Automate referrals from trigger to payout.
                </p>
            </>
        ),
    },


];

